import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { setDriverActive } from '../../services/chauffeurService';
import { toast, ToastContainer } from 'react-toastify';
import { getFileUrl } from '../../config';
import './ChauffeurProfileView.css';

export default function ChauffeurProfileView({ driver: initialDriver, missions = [], incidents = [], documents = [] }) {
  const [driver, setDriver] = useState(initialDriver);
  const [loading, setLoading] = useState(false);

  const toggleActivation = async () => {
    const nextStatus = !driver.active;
    const confirmMsg = nextStatus
      ? "Voulez-vous activer ce compte chauffeur ?"
      : "Voulez-vous désactiver ce compte chauffeur ?";

    if (!window.confirm(confirmMsg)) return;

    try {
      setLoading(true);
      await setDriverActive(driver.id, nextStatus);
      setDriver({ ...driver, active: nextStatus });
      toast.success("Statut mis à jour avec succès");
    } catch (error) {
      toast.error(error.message || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4 mb-5">
      <ToastContainer />
      {/* Header - Enhanced with border and background */}
      <div
        className="d-flex justify-content-between align-items-start mb-4 p-4 rounded"
        style={{
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderLeft: '4px solid #0d6efd'
        }}
      >
        <div>
          <h1 className="h2 mb-2 fw-bold" style={{ color: '#1a1a1a' }}>
            {driver.firstname} {driver.lastname}
          </h1>
          <p className="text-muted mb-0 fs-6">{driver.enterpriseName || 'Indépendant'}</p>
        </div>
        <div className="d-flex gap-2">
          <button
            onClick={toggleActivation}
            disabled={loading}
            className={`btn px-4 py-2 ${driver.active ? 'btn-outline-danger' : 'btn-outline-success'}`}
            style={{ borderRadius: '0.5rem', fontWeight: '500' }}
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            ) : (
              <i className={`bi ${driver.active ? 'bi-person-x' : 'bi-person-check'} me-2`}></i>
            )}
            {driver.active ? "Désactiver le compte" : "Activer le compte"}
          </button>
          <Link
            to={`/chauffeurs/edit/${driver.id}`}
            className="btn btn-primary px-4 py-2"
            style={{
              borderRadius: '8px',
              fontWeight: '500',
              boxShadow: '0 2px 4px rgba(13, 110, 253, 0.2)'
            }}
          >
            <i className="bi bi-pencil-square me-2"></i>
            Éditer le profil
          </Link>
        </div>
      </div>

      <div className="row g-4">
        {/* Left Column - Driver Info */}
        <div className="col-lg-4">
          {/* Driver Card - Enhanced with borders and visual elements */}
          <div
            className="card shadow-sm mb-4"
            style={{
              borderRadius: '0.75rem',
              border: '0.0625rem solid #e9ecef',
              overflow: 'hidden',
              fontSize: '0.9375rem',
              padding: '0.5rem 1.25rem'
            }}
          >
            {/* Card header with colored accent */}
            <div
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                height: '0.375rem'
              }}
            ></div>

            <div className="card-body p-4">
              {/* Avatar with border */}
              <div className="text-center mb-4">
                <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold shadow-sm" style={{
                  width: '6.25rem',
                  height: '6.25rem',
                  fontSize: '2rem',
                  backgroundColor: '#e9ecef',
                  color: '#6c757d',
                  border: '0.25rem solid #fff',
                  boxShadow: '0 0.5rem 1.5rem rgba(0,0,0,0.1)'
                }}>
                  {driver.firstname?.[0]}{driver.lastname?.[0]}
                  {/* Online indicator */}
                  {driver.connectionStatus === 'ONLINE' && (
                    <div className="position-absolute rounded-circle" style={{
                      width: '1.125rem',
                      height: '1.125rem',
                      backgroundColor: driver.active ? '#198754' : '#6c757d',
                      border: '0.1875rem solid white',
                      bottom: '0.3125rem',
                      right: '0.3125rem'
                    }} title={driver.active ? "Actif" : "Inactif"}></div>
                  )}
                </div>
                <h5 className="mb-1 fw-semibold">{driver.firstname} {driver.lastname}</h5>
                <p className="text-muted small mb-0">{driver.email}</p>
              </div>

              {/* Status badges with icons and borders */}
              <div className="d-flex justify-content-center gap-2 mb-4 flex-wrap">
                <span
                  className="badge px-3 py-2"
                  style={{
                    backgroundColor: driver.disponible ? '#d4edda' : '#f8f9fa',
                    color: driver.disponible ? '#155724' : '#6c757d',
                    border: `0.0625rem solid ${driver.disponible ? '#c3e6cb' : '#dee2e6'}`,
                    fontWeight: '500',
                    borderRadius: '0.375rem',
                    fontSize: '0.75rem'
                  }}
                >
                  <i className={`bi ${driver.disponible ? 'bi-check-circle-fill' : 'bi-dash-circle'} me-1`}></i>
                  {driver.disponible ? 'Disponible' : 'Indisponible'}
                </span>
                <span
                  className="badge px-3 py-2"
                  style={{
                    backgroundColor: driver.connectionStatus === 'ONLINE' ? '#d1ecf1' : '#f8f9fa',
                    color: driver.connectionStatus === 'ONLINE' ? '#0c5460' : '#6c757d',
                    border: `0.0625rem solid ${driver.connectionStatus === 'ONLINE' ? '#bee5eb' : '#dee2e6'}`,
                    fontWeight: '500',
                    borderRadius: '0.375rem',
                    fontSize: '0.75rem'
                  }}
                >
                  <i className="bi bi-wifi me-1"></i>
                  {driver.connectionStatus || 'OFFLINE'}
                </span>
                <span
                  className="badge px-3 py-2"
                  style={{
                    backgroundColor: driver.shiftStatus === 'EN_POSTE' ? '#cfe2ff' : '#fff3cd',
                    color: driver.shiftStatus === 'EN_POSTE' ? '#084298' : '#856404',
                    border: `0.0625rem solid ${driver.shiftStatus === 'EN_POSTE' ? '#b6d4fe' : '#ffeaa7'}`,
                    fontWeight: '500',
                    borderRadius: '0.375rem',
                    fontSize: '0.75rem'
                  }}
                >
                  <i className="bi bi-clock me-1"></i>
                  {driver.shiftStatus === 'EN_POSTE' ? 'En poste' : 'Hors poste'}
                </span>
              </div>

              <div
                className="pt-4"
                style={{ borderTop: '0.125rem solid #f0f0f0' }}
              >
                {/* Contact info with icons and borders */}
                <div className="card h-100 border-0 shadow-sm p-3 text-center" style={{
                  border: '0.0625rem solid #e9ecef',
                  borderLeft: '0.1875rem solid #0d6efd'
                }}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-telephone text-primary me-2" style={{ fontSize: '1.1rem' }}></i>
                    <div className="flex-grow-1">
                      <div className="text-muted small">Téléphone</div>
                      <div className="fw-medium">{driver.telephone}</div>
                    </div>
                  </div>
                </div>

                <div className="card h-100 border-0 shadow-sm p-3 text-center" style={{
                  border: '0.0625rem solid #e9ecef',
                  borderLeft: '0.1875rem solid #6c757d'
                }}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-building text-secondary me-2" style={{ fontSize: '1.1rem' }}></i>
                    <div className="flex-grow-1">
                      <div className="text-muted small">Entreprise</div>
                      <div className="fw-medium">{driver.enterpriseName || '—'}</div>
                    </div>
                  </div>
                </div>

                <div className="card h-100 border-0 shadow-sm p-3 text-center" style={{
                  border: '0.0625rem solid #e9ecef',
                  borderLeft: '0.1875rem solid #198754'
                }}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-credit-card text-success me-2" style={{ fontSize: '1.1rem' }}></i>
                    <div className="flex-grow-1">
                      <div className="text-muted small">Permis</div>
                      <div className="fw-medium">{driver.licenseNumber || '—'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Documents Card with enhanced borders */}
          <div
            className="card shadow-sm"
            style={{
              borderRadius: '0.75rem',
              border: '0.0625rem solid #e9ecef'
            }}
          >
            <div
              className="card-header bg-white pt-4 px-4 pb-3"
              style={{ borderBottom: '0.125rem solid #f0f0f0' }}
            >
              <div className="d-flex align-items-center">
                <i className="bi bi-file-earmark-text text-primary me-2" style={{ fontSize: '1.2rem' }}></i>
                <h6 className="mb-0 fw-semibold flex-grow-1" style={{ fontSize: '1rem' }}>Documents</h6>
                <span
                  className="badge rounded-pill"
                  style={{
                    backgroundColor: '#e7f3ff',
                    color: '#0066cc',
                    border: '0.0625rem solid #b3d9ff'
                  }}
                >
                  {documents.length}
                </span>
              </div>
            </div>
            <div className="card-body px-4 pb-4">
              {documents.length === 0 ? (
                <div className="text-center py-4">
                  <div className="p-2 mb-2 bg-light rounded-pill" style={{ borderRadius: '0.375rem' }}>
                    <i className="bi bi-inbox text-muted" style={{ fontSize: '2rem' }}></i>
                  </div>
                  <div className="text-muted small mt-2">Aucun document disponible</div>
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {documents.map((doc) => (
                    <a
                      key={doc.id}
                      href={getFileUrl(doc.fileUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-none p-3 rounded"
                      style={{
                        backgroundColor: '#f8f9fa',
                        border: '0.0625rem solid #e9ecef',
                        borderLeft: '0.1875rem solid #0d6efd',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e9ecef';
                        e.currentTarget.style.borderLeftColor = '#0a58ca';
                        e.currentTarget.style.transform = 'translateX(0.1875rem)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        e.currentTarget.style.borderLeftColor = '#0d6efd';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="flex-grow-1">
                          <div className="fw-medium mb-1" style={{ color: '#1a1a1a', fontSize: '0.9rem' }}>
                            <i className="bi bi-file-pdf text-danger me-2"></i>
                            {doc.name}
                          </div>
                          <small
                            className="badge"
                            style={{
                              backgroundColor: '#fff3cd',
                              color: '#856404',
                              border: '0.0625rem solid #ffeaa7'
                            }}
                          >
                            {doc.type}
                          </small>
                        </div>
                        <i className="bi bi-download" style={{ color: '#6c757d', fontSize: '1.2rem' }}></i>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Missions & Incidents */}
        <div className="col-lg-8">
          {/* Missions Card with enhanced table design */}
          <div
            className="card shadow-sm mb-4"
            style={{
              borderRadius: '0.75rem',
              border: '0.0625rem solid #e9ecef'
            }}
          >
            <div
              className="card-header bg-white pt-4 px-4 pb-3 d-flex justify-content-between align-items-center"
              style={{ borderBottom: '0.125rem solid #f0f0f0' }}
            >
              <div className="d-flex align-items-center">
                <i className="bi bi-geo-alt text-primary me-2" style={{ fontSize: '1.2rem' }}></i>
                <h6 className="mb-0 fw-semibold" style={{ fontSize: '1rem' }}>Missions</h6>
              </div>
              <span
                className="badge px-3 py-2"
                style={{
                  backgroundColor: '#e7f3ff',
                  color: '#0066cc',
                  border: '0.0625rem solid #b3d9ff',
                  fontWeight: '600',
                  borderRadius: '1.25rem'
                }}
              >
                {missions.length} Total
              </span>
            </div>
            <div className="card-body px-4 pb-4">
              {missions.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-calendar-x text-muted" style={{ fontSize: '2.5rem' }}></i>
                  <div className="text-muted mt-3">Aucune mission enregistrée</div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle" style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                    <thead>
                      <tr style={{ borderBottom: '0.125rem solid #e9ecef' }}>
                        <th className="border-0 text-muted fw-semibold small pb-3">Trajet</th>
                        <th className="border-0 text-muted fw-semibold small pb-3">Statut</th>
                        <th className="border-0 text-muted fw-semibold small pb-3 pe-0">Date départ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {missions.map((mission, index) => (
                        <tr
                          key={mission.id}
                          className="position-relative"
                          style={{
                            borderBottom: index !== missions.length - 1 ? '0.0625rem solid #f0f0f0' : 'none'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <td className="border-0 py-3">
                            <div style={{ fontSize: '0.9rem' }}>
                              <i className="bi bi-geo text-success me-1"></i>
                              {mission.departureLocation}
                              <span className="mx-2" style={{ color: '#6c757d' }}>→</span>
                              <i className="bi bi-geo-fill text-danger me-1"></i>
                              {mission.arrivalLocation}
                            </div>
                          </td>
                          <td className="border-0 py-3">
                            <span
                              className="badge px-3 py-2"
                              style={{
                                backgroundColor:
                                  mission.status === 'completed' ? '#d4edda' :
                                    mission.status === 'in_progress' ? '#cfe2ff' : '#f8f9fa',
                                color:
                                  mission.status === 'completed' ? '#155724' :
                                    mission.status === 'in_progress' ? '#084298' : '#6c757d',
                                border: `0.0625rem solid ${mission.status === 'completed' ? '#c3e6cb' :
                                  mission.status === 'in_progress' ? '#b6d4fe' : '#dee2e6'
                                  }`,
                                fontWeight: '500',
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem'
                              }}
                            >
                              {mission.status === 'completed' && <i className="bi bi-check-circle me-1"></i>}
                              {mission.status === 'in_progress' && <i className="bi bi-arrow-repeat me-1"></i>}
                              {mission.status}
                            </span>
                          </td>
                          <td className="border-0 py-3 text-muted pe-0">
                            <i className="bi bi-calendar3 me-1"></i>
                            {mission.departureDate?.slice(0, 10)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Incidents Card with enhanced design */}
          <div
            className="card shadow-sm"
            style={{
              borderRadius: '0.75rem',
              border: '0.0625rem solid #e9ecef'
            }}
          >
            <div
              className="card-header bg-white pt-4 px-4 pb-3 d-flex justify-content-between align-items-center"
              style={{ borderBottom: '0.125rem solid #f0f0f0' }}
            >
              <div className="d-flex align-items-center">
                <i className="bi bi-exclamation-triangle text-warning me-2" style={{ fontSize: '1.2rem' }}></i>
                <h6 className="mb-0 fw-semibold" style={{ fontSize: '1rem' }}>Incidents</h6>
              </div>
              <span
                className="badge px-3 py-2"
                style={{
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  border: '0.0625rem solid #f5c6cb',
                  fontWeight: '600',
                  borderRadius: '1.25rem'
                }}
              >
                {incidents.length} Total
              </span>
            </div>
            <div className="card-body px-4 pb-4">
              {incidents.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-shield-check text-success" style={{ fontSize: '2.5rem' }}></i>
                  <div className="text-muted mt-3">Aucun incident signalé</div>
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {incidents.map((incident) => (
                    <div
                      key={incident.id}
                      className="p-3 rounded position-relative"
                      style={{
                        backgroundColor: '#fff',
                        border: '0.0625rem solid #e9ecef',
                        borderLeft: '0.25rem solid #ffc107',
                        boxShadow: '0 0.0625rem 0.1875rem rgba(0,0,0,0.05)'
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-2 gap-2 flex-wrap">
                            <span
                              className="badge px-3 py-2"
                              style={{
                                backgroundColor: '#fff3cd',
                                color: '#856404',
                                border: '1px solid #ffeaa7',
                                fontWeight: '600',
                                borderRadius: '6px',
                                fontSize: '0.75rem'
                              }}
                            >
                              <i className="bi bi-tag me-1"></i>
                              {incident.category}
                            </span>
                            <small className="text-muted d-flex align-items-center">
                              <i className="bi bi-clock me-1"></i>
                              {new Date(incident.createdAt).toLocaleDateString()}
                            </small>
                          </div>
                          <p className="mb-0" style={{ color: '#495057', lineHeight: '1.6' }}>
                            {incident.description}
                          </p>
                        </div>
                        {incident.photoUrl && (
                          <a
                            href={getFileUrl(incident.photoUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-secondary ms-3"
                            style={{
                              borderRadius: '6px',
                              fontWeight: '500',
                              border: '1px solid #dee2e6'
                            }}
                          >
                            <i className="bi bi-image me-1"></i>
                            Photo
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
